"use client";

import { memo } from "react";
import Flex from "@/shared/components/common/Flex";
import type { ReactNode } from "react";
import staticData from "@/public/data/flight/passengers.json";
import { type Control, useFormContext } from "react-hook-form";
import type { PassengersSchemaType } from "@/shared/schemas/flight/passengersSchema";
import PassengerCard from "../cards/passengerCard/PassengerCards";
import ContactCard from "../cards/ContactCard";
import SubmitCard from "../cards/submitcard/SubmitCard";
import FlightInfoCard from "../cards/FlightInfoCard";
import Typography from "@/shared/components/common/Typography";
import { FlightPassengersIcons } from "@/shared/constants/icons";
import { useGlobalStore } from "@/shared/stores/global-store/GlobalStoreProvider";
import InfoCard from "@/containers/components/cards/infoCard/InfoCard";
import Safarmarket from "@/shared/components/thirParty/Safarmarket";
import { useDeviceType } from "@/shared/context/DeviceContext";
import dynamic from "next/dynamic";
import useAuthToken from "@/shared/hooks/useAuthToken";

const SubmitCardMobile = dynamic(
  () => import("@/containers/components/cards/SubmitCard"),
);

type TCardWrapper = "transfereeInfo" | "purchaserInfo" | "flightInfo";
interface RenderFormsProps {
  searchParams: SearchParams;
  flightDetail: FlightDetail;
  recentPassengers: RecentPassenger[];
  loading: boolean;
  control: Control<PassengersSchemaType>;
  nationality: NationalityRes;
  refundRules: RefundRule[];
}

export default memo(function RenderForms({
  searchParams,
  flightDetail,
  recentPassengers,
  loading,
  control,
  nationality,
  refundRules,
}: RenderFormsProps) {
  const { isLoginModalOn, setIsLoginModalOn } = useGlobalStore(
    (state) => state,
  );

  const { formState, watch } = useFormContext<PassengersSchemaType>();
  const authToken = useAuthToken();

  const {
    forms: {
      formsList,
      passengersForm: { passengerType },
    },
    submitInfo: {
      currency,
      termsAndconditions,
      termsAndconditionsRoundedTrips,
    },
  } = staticData;

  const renderForm: Record<string, ReactNode> = {
    passengers: (
      <PassengerCard
        flightDetail={flightDetail}
        nationality={nationality}
        recentPassengers={recentPassengers}
        changeToggle={setIsLoginModalOn}
      />
    ),
    contact: <ContactCard control={control} />,
  };

  const totalPrice =
    watch("passengers").reduce(
      (prev, { type }) =>
        prev + Object?.values(flightDetail)[0].price_details[type]?.payable,
      0,
    ) +
    (Object?.values(flightDetail)[1]
      ? watch("passengers").reduce(
          (prev, { type }) =>
            prev + Object?.values(flightDetail)[1].price_details[type].payable,
          0,
        )
      : 0);

  return (
    <Flex className="flex-col w-full gap-y-2">
      {searchParams.safarmarketId && <Safarmarket />}
      <Flex className="flex-col gap-y-3">
        <FlightInfoCard
          flightDetail={flightDetail}
          searchParams={searchParams}
          refundRules={refundRules}
        />
        {formsList.map(({ Icon, title, variant }) => (
          <InfoCard
            key={title}
            header={{ title, icon: FlightPassengersIcons[Icon] }}
            defaultStyle={3}
          >
            {renderForm[variant as TCardWrapper]}
          </InfoCard>
        ))}
        {useDeviceType() === "mobile" &&
        (!isLoginModalOn || (isLoginModalOn && !!authToken)) ? (
          <SubmitCardMobile
            isLoading={loading}
            totalPrice={totalPrice}
            termsAndConditionMessage={
              searchParams.flightType === "one-way"
                ? termsAndconditions
                : termsAndconditionsRoundedTrips
            }
            validationError={Object.keys(formState.errors).length > 0}
            content={watch("passengers").map(({ na_code, type, price }) => (
              <Flex key={na_code} className="justify-between items-center">
                <Typography className="text-muted-foreground">
                  {passengerType.find(({ value }) => type === value)?.title ??
                    ""}
                </Typography>
                <Flex className="gap-x-1 items-center">
                  <Typography className="text-muted-foreground">
                    {price.toLocaleString()}
                  </Typography>
                  <Typography className="text-muted-foreground text-xs">
                    {currency}
                  </Typography>
                </Flex>
              </Flex>
            ))}
          />
        ) : (
          <SubmitCard
            totalPrice={totalPrice}
            isLoading={loading}
            validationError={Object.keys(formState.errors).length > 0}
          />
        )}
      </Flex>
    </Flex>
  );
});
